<template>
  <div class="container">
    <!-- Loading Spinner -->
    <div
      v-if="areRoomLimitsLoading"
      class="d-flex justify-content-center align-content-center my-6"
      role="status"
      aria-live="polite"
    >
      <CSpinner color="primary" aria-label="Loading content" />
    </div>
    <!-- Loading Spinner -->

    <!-- Read-only view (tab): ul/li with styled lists -->
    <div v-else-if="isInView" class="autopass-readonly">
      <div class="autopass-readonly-section">
        <Label>START / STOP by student</Label>
        <ul v-if="startAndStopSelected.length" class="autopass-readonly-list">
          <li
            v-for="item in startAndStopSelected"
            :key="item.value.id"
            class="autopass-readonly-item"
          >
            {{ item.label }}
          </li>
        </ul>
        <p v-else class="autopass-readonly__empty autopass-readonly-list">—</p>
      </div>
      <div class="autopass-readonly-section">
        <Label>only START by student</Label>
        <ul v-if="startOnlySelected.length" class="autopass-readonly-list">
          <li
            v-for="item in startOnlySelected"
            :key="item.value.id"
            class="autopass-readonly-item"
          >
            {{ item.label }}
          </li>
        </ul>
        <p v-else class="autopass-readonly__empty autopass-readonly-list">—</p>
      </div>
      <div class="autopass-readonly-section">
        <Label>only STOP by student</Label>
        <ul v-if="stopOnlySelected.length" class="autopass-readonly-list">
          <li
            v-for="item in stopOnlySelected"
            :key="item.value.id"
            class="autopass-readonly-item"
          >
            {{ item.label }}
          </li>
        </ul>
        <p v-else class="autopass-readonly__empty autopass-readonly-list">—</p>
      </div>
      <div class="autopass-readonly-section">
        <Label>Limit</Label>
        <ul class="autopass-readonly-list">
          <li class="autopass-readonly-item">{{ limitSelectedLabel }}</li>
        </ul>
      </div>
    </div>
    <!-- Read-only view -->

    <!-- Form Content (modal / edit mode) -->
    <form v-else class="d-flex flex-column gap-4" @submit.prevent="submit">
      <!-- START / STOP by student -->
      <div>
        <Label for="start-stop-select">START / STOP by student</Label>
        <CustomSelect
          id="start-stop-select"
          v-if="rooms"
          v-model="form.startAndStop"
          :invalid-feedback="serverErrors?.roomId || errors?.startAndStop"
          :options="startAndStopRooms"
          :filterable="true"
          placeholder="Select location"
          :clearable="false"
          :reduce="(item) => item.value.id"
          multiple
          aria-required="false"
          aria-describedby="start-stop-error"
          :clear-search-on-blur="true"
          :key="componentKey"
        />
        <InfoBox
          class="danger mt-2"
          v-if="serverErrors?.roomId"
          id="start-stop-error"
        >
          {{ serverErrors?.roomId?.[0] || "" }}
        </InfoBox>
      </div>
      <!-- START / STOP by student -->

      <!-- only START by student -->
      <div>
        <Label for="start-only-select">only START by student</Label>
        <CustomSelect
          id="start-only-select"
          v-if="rooms"
          :invalid-feedback="
            (serverErrors?.roomId ? serverErrors?.roomId : '') ||
            errors?.startOnly
          "
          v-model="form.startOnly"
          :options="startOnlyRooms"
          :filterable="true"
          placeholder="Select location"
          :clearable="false"
          :reduce="(item) => item.value.id"
          multiple
          aria-required="false"
          aria-describedby="start-only-error"
          :clear-search-on-blur="true"
          :key="componentKey"
        />
        <InfoBox
          class="danger mt-2"
          v-if="serverErrors?.roomId"
          id="start-only-error"
        >
          {{ serverErrors?.roomId?.[0] || "" }}
        </InfoBox>
      </div>
      <!-- only START by student -->

      <!-- only STOP by student -->
      <div>
        <Label for="stop-only-select">only STOP by student</Label>
        <CustomSelect
          id="stop-only-select"
          v-if="rooms"
          :invalid-feedback="serverErrors?.roomId || errors?.stopOnly"
          v-model="form.stopOnly"
          :options="stopOnlyRooms"
          placeholder="Select location"
          :clearable="false"
          :reduce="(item) => item.value.id"
          :filterable="true"
          multiple
          aria-required="false"
          aria-describedby="stop-only-error"
          :clear-search-on-blur="true"
          :key="componentKey"
        />
        <InfoBox
          class="danger mt-2"
          v-if="serverErrors?.roomId"
          id="stop-only-error"
        >
          {{ serverErrors?.roomId?.[0] || "" }}
        </InfoBox>
      </div>
      <!-- only STOP by student -->

      <!-- Set limit -->
      <div>
        <Label for="limit-select" required>Set limit:</Label>
        <CustomSelect
          id="limit-select"
          class="w-50"
          v-model="form.limit"
          :invalid-feedback="serverErrors?.limit || errors?.limit"
          :options="limitOptions"
          :filterable="true"
          placeholder="Select limit"
          :clearable="false"
          :reduce="(item) => item.value.toString()"
          :disabled="!isSelectedRoom"
          aria-required="true"
          aria-describedby="limit-error"
          :clear-search-on-blur="true"
        />
        <InfoBox
          class="danger mt-2"
          v-if="serverErrors?.limit"
          id="limit-error"
        >
          {{ serverErrors?.limit?.[0] || "" }}
        </InfoBox>
      </div>
      <!-- Set limit -->

      <!-- Server Response InfoBox -->
      <InfoBox
        v-if="serverRes"
        :class="{ danger: Object.keys(serverErrors).length }"
        :title="serverRes.title"
        role="alert"
        aria-live="assertive"
      >
        {{ serverRes.message }}
      </InfoBox>
      <!-- Server Response InfoBox -->

      <!-- Cancel and Submit Buttons (hidden when in tab / read-only view) -->
      <div
        v-if="!isInView"
        class="d-flex justify-content-center align-content-center gap-4"
      >
        <!-- Cancel Button -->
        <BaseButton
          type="button"
          rounded
          @click="$emit('cancel')"
          aria-label="Cancel form submission"
        >
          Cancel
        </BaseButton>
        <!-- Cancel Button -->

        <!-- Submit Button -->
        <LoadingButton
          :is-loading="isLoading"
          @click="submit"
          solid
          rounded
          type="submit"
          aria-label="Submit form"
        >
          Submit
        </LoadingButton>
        <!-- Submit Button -->
      </div>
      <!-- Cancel and Submit Buttons -->
    </form>
    <!-- Form Content -->
  </div>
</template>

<script>
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import useVuelidate from "@vuelidate/core"
import { helpers, required } from "@vuelidate/validators"
import { computed, onMounted, reactive, ref } from "vue"
import { useStore } from "vuex"

export default {
  name: "CreateAutoPassLimit",
  components: { InfoBox, LoadingButton, BaseButton, CustomSelect, Label },
  props: {
    limit: {
      type: [String, Number],
      default: -1
    },
    isInView: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [Number, String],
      default: null
    }
  },
  emits: ["cancel", "closeModal"],
  setup(props, { emit }) {
    const store = useStore()
    const componentKey = ref(0)

    const form = reactive({
      startAndStop: [],
      startOnly: [],
      stopOnly: [],
      limit: {}
    })

    const serverErrors = reactive({})
    const serverRes = ref(null)
    const isLoading = ref(false)
    const areRoomLimitsLoading = ref(true)

    const rooms = computed(
      () => store.getters["dashboardTable/formListAutoPassesRooms"]
    )
    const autoPassLimits = computed(() => store.getters["rooms/autoPassLimits"])

    function getLimitFromData(obj) {
      if (obj == null) return undefined
      if (
        typeof obj.limit === "number" ||
        (typeof obj.limit === "string" && obj.limit !== "")
      )
        return Number(obj.limit)
      if (obj.auto_pass?.limit != null) return Number(obj.auto_pass.limit)
      if (obj.auto_pass_limit != null) return Number(obj.auto_pass_limit)
      const prefs = obj.auto_pass_preference_to_user
      if (prefs?.length) {
        const first = prefs[0]
        if (first?.limit != null) return Number(first.limit)
        if (first?.auto_pass?.limit != null)
          return Number(first.auto_pass.limit)
        for (let i = 0; i < prefs.length; i++) {
          const lim = prefs[i]?.auto_pass?.limit ?? prefs[i]?.limit
          if (lim != null) return Number(lim)
        }
      }
      return undefined
    }

    const limitOptions = computed(() => {
      const options = [
        { label: "Turn off", value: "-1" },
        { label: "No limit", value: "0" }
      ]
      for (let i = 1; i < 21; i++) {
        options.push({ label: i, value: `${i}` })
      }
      return options
    })

    const startAndStopRooms = computed(() => {
      return form.startOnly.length || form.stopOnly.length
        ? rooms.value.filter(
            (el) =>
              !form.startOnly.includes(el.value.id) &&
              !form.stopOnly.includes(el.value.id)
          )
        : rooms.value
    })

    const startOnlyRooms = computed(() => {
      return form.startAndStop.length || form.stopOnly.length
        ? rooms.value.filter(
            (el) =>
              !form.startAndStop.includes(el.value.id) &&
              !form.stopOnly.includes(el.value.id)
          )
        : rooms.value
    })

    const stopOnlyRooms = computed(() => {
      return form.startAndStop.length || form.startOnly.length
        ? rooms.value.filter(
            (el) =>
              !form.startAndStop.includes(el.value.id) &&
              !form.startOnly.includes(el.value.id)
          )
        : rooms.value
    })

    const isSelectedRoom = computed(() => {
      return Boolean(
        form.startAndStop.length ||
        form.startOnly.length ||
        form.stopOnly.length
      )
    })

    const startAndStopSelected = computed(() => {
      if (!startAndStopRooms.value || !form.startAndStop?.length) return []
      return startAndStopRooms.value.filter((r) =>
        form.startAndStop.includes(r.value.id)
      )
    })

    const startOnlySelected = computed(() => {
      if (!startOnlyRooms.value || !form.startOnly?.length) return []
      return startOnlyRooms.value.filter((r) =>
        form.startOnly.includes(r.value.id)
      )
    })

    const stopOnlySelected = computed(() => {
      if (!stopOnlyRooms.value || !form.stopOnly?.length) return []
      return stopOnlyRooms.value.filter((r) =>
        form.stopOnly.includes(r.value.id)
      )
    })

    const limitSelectedLabel = computed(() => {
      const opt = limitOptions.value?.find(
        (o) => String(o.value) === String(form.limit)
      )
      return opt?.label ?? form.limit ?? "—"
    })

    const validationMessages = {
      required: "This field is required"
    }

    const validations = {
      form: {
        startAndStop: {},
        startOnly: {},
        stopOnly: {},
        limit: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }

    const v$ = useVuelidate(validations, { form })

    const errors = computed(() => {
      const errorObj = {}
      v$?.value?.$errors?.forEach((err) => {
        errorObj[err.$property] = err.$message
      })
      return errorObj
    })

    onMounted(async () => {
      form.limit = String(limitOptions.value?.[1]?.value || "-1")
      try {
        const response = await (props.isInView && props.userId
          ? store.dispatch("rooms/getAutoPassLimitByUserId", props.userId)
          : store.dispatch("rooms/getAutoPassLimit"))
        const data = response.data.data
        if (data?.auto_pass_preference_to_user?.length) {
          setAutoPassLimitData(data)
        }
        if (data?.rooms?.length) {
          const newRooms = data.rooms.map((room) => {
            return {
              room: {
                id: room.id,
                name: room.name
              }
            }
          })
          store.commit("dashboardTable/SET_AUTO_PASSES_ROOMS", newRooms)
          const newStats = store.getters["dashboardTable/stats"]
          store.commit("dashboardTable/SET_STATS", {
            ...newStats,
            autoPassesRoomsCount: newRooms.length
          })
        }
      } catch {
        setResponseInfoBox("Error", "Failed to fetch auto pass limit data.")
      } finally {
        areRoomLimitsLoading.value = false
      }
    })

    const setAutoPassLimitData = (data) => {
      const modeMapping = {
        start_and_stop: "startAndStop",
        start_only: "startOnly",
        stop_only: "stopOnly"
      }
      data.auto_pass_preference_to_user.forEach((el) => {
        const modeKey = modeMapping[el.mode]
        if (
          el.auto_pass.room_id &&
          !form.startOnly.includes(el.auto_pass.room_id) &&
          !form.stopOnly.includes(el.auto_pass.room_id) &&
          !form.startAndStop.includes(el.auto_pass.room_id) &&
          modeKey
        ) {
          form[modeKey].push(el.auto_pass.room_id)
        }
      })
      if (props.isInView && props.userId) {
        const limitFromData = getLimitFromData(data)
        if (limitFromData != null) {
          form.limit = String(limitFromData)
        } else {
          form.limit = String(props.limit ?? -1)
        }
      } else {
        form.limit = String(props.limit ?? -1)
      }
      componentKey.value++
    }

    const submit = async () => {
      setResponseInfoBox()
      const isFormCorrect = await v$.value.$validate()
      if (!isFormCorrect) {
        return
      }

      const data = {
        limit: parseInt(form.limit),
        rooms_and_modes: []
      }
      if (form.startAndStop.length) {
        form.startAndStop.forEach((room) => {
          data.rooms_and_modes.push({ room_id: room, mode: "start_and_stop" })
        })
      }
      if (form.startOnly.length) {
        form.startOnly.forEach((room) => {
          data.rooms_and_modes.push({ room_id: room, mode: "start_only" })
        })
      }
      if (form.stopOnly.length) {
        form.stopOnly.forEach((room) => {
          data.rooms_and_modes.push({ room_id: room, mode: "stop_only" })
        })
      }
      isLoading.value = true
      try {
        await store.dispatch("rooms/addAutoPassLimit", data)
        setResponseInfoBox("Success", "Successfully saved!")
        await refreshAutoPassLimits()
        setResponseInfoBox()
        emit("cancel")
      } catch (err) {
        const res = err.response.data
        Object.assign(serverErrors, res.errors ? res.errors : {})
        // Extract rooms_and_modes.*.room_id errors
        let roomsAndModesErrors = null
        Object.keys(res.errors).forEach((key) => {
          const match = key.match(/^rooms_and_modes\.(\d+)\.room_id$/)
          if (match) {
            roomsAndModesErrors = res.errors[key][0]
          }
        })
        if (roomsAndModesErrors) {
          serverErrors.message = roomsAndModesErrors
          setResponseInfoBox("Error", roomsAndModesErrors)
        } else {
          setResponseInfoBox("Error", res.message)
        }
      } finally {
        isLoading.value = false
      }
    }

    const refreshAutoPassLimits = async () => {
      try {
        const res = await store.dispatch("dashboardTable/getDashboard")
        const data = res.data.data
        if (data?.autoPassesPreference) {
          const autoPassPreferenceToUser = data.autoPassesPreference
          store.commit("rooms/SET_AUTO_PASS_LIMITS", {
            ...autoPassLimits.value,
            auto_pass_preference_to_user: autoPassPreferenceToUser
          })
        }
      } catch {
        setResponseInfoBox("Error", "Failed to refresh auto pass limits.")
      }
    }

    const setResponseInfoBox = (title, message) => {
      if (title || message) {
        serverRes.value = { message, title }
      } else {
        serverRes.value = null
      }
    }

    return {
      form,
      serverErrors,
      serverRes,
      isLoading,
      areRoomLimitsLoading,
      rooms,
      autoPassLimits,
      limitOptions,
      startAndStopRooms,
      startOnlyRooms,
      stopOnlyRooms,
      isSelectedRoom,
      startAndStopSelected,
      startOnlySelected,
      stopOnlySelected,
      limitSelectedLabel,
      setAutoPassLimitData,
      submit,
      refreshAutoPassLimits,
      errors,
      componentKey
    }
  }
}
</script>

<style scoped>
.autopass-readonly {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.autopass-readonly-section {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.autopass-readonly-list {
  list-style: disc;
  margin: 0;
  padding: 0.75rem 1rem 0.75rem 1.75rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem 1rem;
  background: var(--cui-tertiary-bg, #e9ecef);
  border-radius: 0.25rem;
}

.autopass-readonly-item {
  padding: 0.25rem 0;
  font-size: 0.9375rem;
  color: var(--cui-body-color, #212529);
  line-height: 1.4;
}
</style>
